import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Check, Paperclip, Plus, Send, Trash2, X } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Selo } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { AreaSensivel, Formulario, Rotulo, Selecao, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Drawer, Modal } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { api, del, get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { data, dataHora, dinheiro, hojeIso, nomeDeArquivo, nomePessoa, textoLivre } from '../../nucleo/formato';
import { comMaisOpcoes } from '../../nucleo/modo-simples';
import { useSessao } from '../../nucleo/sessao';
import { COR_ESTADO, ESTADOS, invalidarDespesas, type Classificacao, type Despesa, type Estado } from './tipos';

/** Despesas (todas, com filtro de situação) e Aprovações (as enviadas, com a decisão do gestor). */
function ListaDespesas({ aprovacoes }: { aprovacoes: boolean }) {
  const { t } = useTranslation('despesas');
  const s = useSessao();
  const [estado, setEstado] = useState<Estado | ''>('');
  const [aberta, setAberta] = useState<Despesa | 'nova' | null>(null);
  const colunas: Coluna<Despesa>[] = [
    { chave: 'descricao', rotulo: t('campos.descricao'), principal: true, render: (d) => <span className="font-medium">{textoLivre(d.descricao, d.funcionario_nome)}</span> },
    { chave: 'funcionario_nome', rotulo: t('campos.funcionario'), render: (d) => nomePessoa(d.funcionario_nome) || '-' },
    { chave: 'data', rotulo: t('campos.data'), render: (d) => data(d.data) },
    { chave: 'categoria_nome', rotulo: t('campos.categoria'), render: (d) => d.categoria_nome ?? <span className="text-tinta-3">{t('semAcesso')}</span> },
    { chave: 'centro_custo_nome', rotulo: t('campos.centro'), oculta: true, render: (d) => (d.centro_custo_id ? (d.centro_custo_nome ?? t('semAcesso')) : '-') },
    { chave: 'valor_centavos', rotulo: t('campos.valor'), alinhar: 'direita', mono: true, render: (d) => dinheiro(d.valor_centavos) },
    { chave: 'estado', rotulo: t('campos.estado'), render: (d) => <Selo cor={COR_ESTADO[d.estado]}>{t(`estado.${d.estado}`)}</Selo> },
  ];
  const params = useMemo(() => (aprovacoes ? { estado: 'enviada' } : estado ? { estado } : {}), [aprovacoes, estado]);
  const props = {
    caminho: '/despesas/despesas',
    params,
    chaveConsulta: ['despesas', 'lista', aprovacoes ? 'aprovacoes' : 'todas', estado],
    colunas,
    aoClicar: (d: Despesa) => setAberta(d),
    buscaNaUrl: true,
    filtros: aprovacoes ? undefined : { estado },
    aoAplicarFiltros: aprovacoes ? undefined : (f: Record<string, unknown>) => setEstado(ESTADOS.includes(f.estado as Estado) ? (f.estado as Estado) : ''),
    filtrosExtra: aprovacoes ? undefined : (
      <Selecao
        aria-label={t('campos.estado')}
        value={estado}
        onChange={(e) => setEstado(e.target.value as Estado | '')}
        opcoes={[{ valor: '', rotulo: t('estado.todos') }, ...ESTADOS.map((x) => ({ valor: x, rotulo: t(`estado.${x}`) }))]}
      />
    ),
    vazio: (
      <Vazio
        texto={aprovacoes ? t('vazio.aprovacoes') : t('vazio.despesas')}
        acao={
          !aprovacoes && !estado && s.pode('despesas.criar') ? (
            <Botao icone={<Plus className="size-4" />} onClick={() => setAberta('nova')}>
              {t('acoes.novaDespesa')}
            </Botao>
          ) : undefined
        }
      />
    ),
  };
  return (
    <>
      {!aprovacoes && s.pode('despesas.criar') && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setAberta('nova')}>
            {t('acoes.novaDespesa')}
          </Botao>
        </AcaoPrimaria>
      )}
      {aprovacoes ? <Tabela<Despesa> tela="despesas.aprovacoes" {...props} /> : <Tabela<Despesa> tela="despesas.despesas" {...props} />}
      {aberta === 'nova' && <FormDespesa despesa={null} aoFechar={() => setAberta(null)} />}
      {aberta && aberta !== 'nova' && (aberta.pode_editar ? <FormDespesa despesa={aberta} aoFechar={() => setAberta(null)} /> : <FichaDespesa despesa={aberta} aoFechar={() => setAberta(null)} />)}
    </>
  );
}

export const Despesas = () => <ListaDespesas aprovacoes={false} />;
export const Aprovacoes = () => <ListaDespesas aprovacoes />;

/** Lançar ou editar (rascunho ou recusada): o comprovante sobe antes, com o id da despesa (anexo do núcleo). */
export function FormDespesa({ despesa, aoFechar }: { despesa: Despesa | null; aoFechar: () => void }) {
  const { t } = useTranslation('despesas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const id = useMemo(() => despesa?.id ?? crypto.randomUUID(), [despesa]);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [remover, setRemover] = useState(false);
  const enviarDepois = useRef(false);
  const anexoEnviado = useRef<string | null>(null);
  const seletor = useRef<HTMLInputElement>(null);
  const cls = useQuery({ queryKey: ['despesas', 'classificacao'], queryFn: () => get<Classificacao>('/despesas/classificacao') });
  const excluir = useMutation({
    mutationFn: () => del(`/despesas/despesas/${id}`),
    onSuccess: async () => {
      await invalidarDespesas(qc);
      aoFechar();
    },
    onError: (e) => {
      setRemover(false);
      toast.erro(mensagemDeErro(e));
    },
  });
  const campos: DefCampo[] = [
    ...(!despesa && s.pode('despesas.editar')
      ? [{ nome: 'funcionario_id', rotulo: t('campos.funcionario'), tipo: 'busca', ajuda: t('campos.funcionarioAjuda'), busca: { caminho: '/pessoas/funcionarios', rotulo: (i: Record<string, unknown>) => nomePessoa(String(i.nome ?? '')) } } as DefCampo]
      : []),
    { nome: 'descricao', rotulo: t('campos.descricao'), tipo: 'texto', obrigatorio: true },
    { nome: 'data', rotulo: t('campos.data'), tipo: 'data', obrigatorio: true, largura: 'meia' },
    { nome: 'valor_centavos', rotulo: t('campos.valor'), tipo: 'dinheiro', obrigatorio: true, largura: 'meia' },
    { nome: 'categoria_id', rotulo: t('campos.categoria'), tipo: 'selecao', obrigatorio: true, largura: 'meia', opcoes: (cls.data?.categorias ?? []).map((c) => ({ valor: c.id, rotulo: c.codigo ? `${c.codigo} ${c.nome}` : c.nome })) },
    { nome: 'centro_custo_id', rotulo: t('campos.centro'), tipo: 'selecao', largura: 'meia', opcoes: (cls.data?.centros ?? []).map((c) => ({ valor: c.id, rotulo: c.nome })) },
  ];
  const inicial = despesa
    ? { descricao: despesa.descricao, data: despesa.data, valor_centavos: despesa.valor_centavos, categoria_id: despesa.categoria_id, centro_custo_id: despesa.centro_custo_id ?? '' }
    : { data: hojeIso() };
  // U15 F5: no modo simples (despesa nova), "em nome de" (funcionário; vazio vale a própria pessoa) e o centro de custo ficam
  // em "Mais opções"; nenhum é obrigatório. Categoria, data, valor e descrição seguem à vista.
  const maisOpcoes = comMaisOpcoes(campos, { ativo: s.modoSimples && !despesa, avancados: ['funcionario_id', 'centro_custo_id'], rotulo: tn('comum.maisOpcoes'), inicial });
  return (
    <>
      <Drawer
        aberto
        aoFechar={aoFechar}
        largura="lg"
        titulo={despesa ? textoLivre(despesa.descricao, despesa.funcionario_nome) : t('acoes.novaDespesa')}
        rodape={
          <>
            {despesa && (
              <Botao variante="perigo" icone={<Trash2 className="size-4" />} onClick={() => setRemover(true)}>
                {t('acoes.excluir')}
              </Botao>
            )}
            <Botao type="submit" form="form-despesa" onClick={() => (enviarDepois.current = false)}>
              {t('acoes.salvarRascunho')}
            </Botao>
            <Botao variante="primario" icone={<Send className="size-4" />} type="submit" form="form-despesa" onClick={() => (enviarDepois.current = true)}>
              {t('acoes.enviar')}
            </Botao>
          </>
        }
      >
        {despesa?.estado === 'recusada' && despesa.motivo_decisao && (
          <p className="mb-3 rounded-lg border border-borda px-3 py-2 text-sm text-tinta-2">
            {t('campos.motivo')}: {textoLivre(despesa.motivo_decisao, despesa.funcionario_nome)}
          </p>
        )}
        {cls.isPending ? (
          <Carregando />
        ) : cls.isError ? (
          <Erro erro={cls.error} aoTentar={() => cls.refetch()} />
        ) : (
          <Formulario
            id="form-despesa"
            campos={maisOpcoes.campos}
            gruposRecolhiveis={maisOpcoes.gruposRecolhiveis}
            recolhiveisAoFim
            inicial={inicial}
            aoEnviar={async (v) => {
              if (arquivo && !anexoEnviado.current) {
                const fd = new FormData();
                fd.append('entidade', 'despesas.despesa');
                fd.append('registro_id', id);
                fd.append('arquivo', arquivo);
                anexoEnviado.current = (await api<{ id: string }>('POST', '/anexos', fd)).id;
              }
              const comprovante = anexoEnviado.current ?? despesa?.comprovante_anexo_id ?? null;
              const corpo = { descricao: v.descricao, data: v.data, valor_centavos: v.valor_centavos, categoria_id: v.categoria_id, centro_custo_id: v.centro_custo_id || null, comprovante_anexo_id: comprovante };
              if (despesa) {
                await patch(`/despesas/despesas/${id}`, corpo);
                if (enviarDepois.current) await post(`/despesas/despesas/${id}/enviar`);
              } else {
                await post('/despesas/despesas', { id, funcionario_id: v.funcionario_id || null, ...corpo, enviar: enviarDepois.current }, { 'Idempotency-Key': id });
              }
              await invalidarDespesas(qc);
              toast.sucesso(tn('comum.salvo'));
              aoFechar();
            }}
          >
            <div className="flex flex-col gap-1.5 sm:col-span-6">
              <span className="text-sm font-medium text-tinta-2">{t('campos.comprovante')}</span>
              <div className="flex min-w-0 flex-wrap items-center gap-3">
                <Botao icone={<Paperclip className="size-4" />} onClick={() => seletor.current?.click()}>
                  {arquivo || despesa?.comprovante_anexo_id ? t('acoes.trocarArquivo') : t('acoes.anexar')}
                </Botao>
                {(arquivo || despesa?.comprovante_nome) && (
                  <span className="min-w-0 truncate text-sm text-tinta-2" title={nomeDeArquivo(arquivo?.name ?? despesa?.comprovante_nome, despesa?.funcionario_nome)}>
                    {nomeDeArquivo(arquivo?.name ?? despesa?.comprovante_nome, despesa?.funcionario_nome)}
                  </span>
                )}
                <input
                  ref={seletor}
                  type="file"
                  className="sr-only"
                  tabIndex={-1}
                  aria-label={t('campos.comprovante')}
                  title={t('campos.comprovante')}
                  accept="image/*,.pdf"
                  onChange={(e) => {
                    anexoEnviado.current = null;
                    setArquivo(e.target.files?.[0] ?? null);
                  }}
                />
              </div>
            </div>
          </Formulario>
        )}
      </Drawer>
      <Confirmar aberto={remover} aoFechar={() => setRemover(false)} aoConfirmar={() => excluir.mutate()} titulo={t('confirmar.excluir')} texto={t('confirmar.excluirTexto')} perigoso carregando={excluir.isPending} />
    </>
  );
}

/** Ficha só de leitura, com a decisão do gestor quando a despesa está enviada. */
export function FichaDespesa({ despesa, aoFechar }: { despesa: Despesa; aoFechar: () => void }) {
  const { t } = useTranslation('despesas');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [recusando, setRecusando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const acao = useMutation({
    mutationFn: ({ qual, corpo }: { qual: 'aprovar' | 'recusar'; corpo?: unknown }) => post(`/despesas/despesas/${despesa.id}/${qual}`, corpo),
    onSuccess: async () => {
      await invalidarDespesas(qc);
      toast.sucesso(tn('comum.salvo'));
      aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const linha = (rotulo: string, valor: React.ReactNode) => (
    <div className="flex flex-wrap justify-between gap-2 border-b border-borda py-2 text-sm last:border-0">
      <dt className="text-tinta-3">{rotulo}</dt>
      <dd className="min-w-0 text-right text-tinta-1">{valor}</dd>
    </div>
  );
  return (
    <>
      <Drawer
        aberto={!recusando}
        aoFechar={aoFechar}
        titulo={textoLivre(despesa.descricao, despesa.funcionario_nome)}
        rodape={
          despesa.pode_decidir ? (
            <>
              <Botao icone={<X className="size-4" />} onClick={() => setRecusando(true)}>
                {t('acoes.recusar')}
              </Botao>
              <Botao variante="primario" icone={<Check className="size-4" />} carregando={acao.isPending} onClick={() => acao.mutate({ qual: 'aprovar' })}>
                {t('acoes.aprovar')}
              </Botao>
            </>
          ) : (
            <Botao variante="fantasma" onClick={aoFechar}>
              {tn('comum.fechar')}
            </Botao>
          )
        }
      >
        <dl>
          {linha(t('campos.estado'), <Selo cor={COR_ESTADO[despesa.estado]}>{t(`estado.${despesa.estado}`)}</Selo>)}
          {linha(t('campos.funcionario'), nomePessoa(despesa.funcionario_nome) || '-')}
          {linha(t('campos.data'), data(despesa.data))}
          {linha(t('campos.valor'), dinheiro(despesa.valor_centavos))}
          {linha(t('campos.categoria'), despesa.categoria_nome ?? t('semAcesso'))}
          {linha(t('campos.centro'), despesa.centro_custo_id ? (despesa.centro_custo_nome ?? t('semAcesso')) : '-')}
          {linha(
            t('campos.comprovante'),
            despesa.comprovante_anexo_id ? (
              <a className="text-acento-forte hover:underline" download href={`/api/v1/anexos/${despesa.comprovante_anexo_id}`}>
                {t('acoes.baixarComprovante')}
              </a>
            ) : (
              '-'
            ),
          )}
          {despesa.decidido_em && linha(t('campos.decididoPor'), `${nomePessoa(despesa.decidido_por_nome) || '-'} · ${dataHora(despesa.decidido_em)}`)}
          {despesa.motivo_decisao && linha(t('campos.motivo'), textoLivre(despesa.motivo_decisao, despesa.funcionario_nome))}
        </dl>
      </Drawer>
      <Modal
        aberto={recusando}
        aoFechar={() => setRecusando(false)}
        titulo={t('recusar.titulo')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setRecusando(false)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" carregando={acao.isPending} aria-disabled={!motivo.trim()} onClick={() => motivo.trim() && acao.mutate({ qual: 'recusar', corpo: { motivo } })}>
              {t('acoes.recusar')}
            </Botao>
          </>
        }
      >
        <Rotulo id="despesa-motivo" rotulo={t('campos.motivo')} obrigatorio ajuda={t('recusar.ajuda')}>
          <AreaSensivel
            id="despesa-motivo"
            rows={3}
            maxLength={500}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            className="w-full min-w-0 rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco"
          />
        </Rotulo>
      </Modal>
    </>
  );
}
